<script lang="ts">
import { defineComponent } from 'vue';
import { useStakesStore } from '@/stores/stakes';
import { mapActions, mapState, mapWritableState } from 'pinia';
import Pagination from '@/components/pagination.vue';
import type { ApiStakeItem } from '@/types/stakes';
import Decimal from 'decimal.js-light';
import { formatCurrency, formatDate, formatMoney } from '@/utils/format';
import { useCurrencyStore } from '@/stores/currencies';
import Headline from '@/components/headline.vue';
import { useSportsStore } from '@/stores/sports';
import ClipboardIcon from '@/components/clipboard-icon.vue';

export default defineComponent({
  name: 'stakes',
  components: { ClipboardIcon, Pagination, Headline },
  computed: {
    ...mapState(useSportsStore, ['sports']),
    ...mapState(useCurrencyStore, ['currencyList']),
    ...mapWritableState(useStakesStore, [
      'count',
      'offset',
      'size',
      'skipEmpty',
      'skipBanned',
      'skipNonCurrency',
      'detailSports',
      'detailCurrencies',
      'detailUsers',
      'fromDate',
      'toDate',
      'period',
      'sportIds',
      'userIds',
      'currencies',
    ]),
    ...mapState(useStakesStore, ['records', 'loading']),
    getCurrencies() {
      return this.currencyList.map((c) => ({ name: c }));
    },
    getRecordsGrouped() {
      if (!this.records) return [];

      const days: {
        [date: string]: {
          totals: ApiStakeItem;
          rows: ApiStakeItem[];
        };
      } = {};

      const daysUsers: { [date: string]: Set<number> } = {};

      // Weird but linter doesn't see the first check
      for (const record of this.records as unknown as ApiStakeItem[]) {
        if (!days[record.date]) {
          days[record.date] = {
            totals: { ...record },
            rows: [record],
          };

          daysUsers[record.date] = new Set();

          if ((record.user_id as number[])?.length) {
            for (const u of (record.user_id as number[]).filter((v) => v)) {
              daysUsers[record.date].add(u);
            }
          } else {
            daysUsers[record.date].add(record.user_id as number);
          }

          continue;
        }

        const day = days[record.date];

        if ((record.user_id as number[])?.length) {
          for (const u of (record.user_id as number[]).filter((v) => v)) {
            daysUsers[record.date].add(u);
          }
        } else {
          daysUsers[record.date].add(record.user_id as number);
        }

        day.rows.push(record);

        if (!day.totals.currency && record.currency) {
          day.totals.currency = record.currency;
        }
        if (day.totals.currency !== record.currency) {
          day.totals.currency = '·';
        }

        day.totals.stakes += record.stakes;
        day.totals.coupons += record.coupons;

        day.totals.stake_amount = new Decimal(day.totals.stake_amount || 0)
          .plus(record.stake_amount || 0)
          .toString();
        day.totals.max_payout = new Decimal(day.totals.max_payout || 0)
          .plus(record.max_payout || 0)
          .toString();
        day.totals.paid_out = new Decimal(day.totals.paid_out || 0)
          .plus(record.paid_out || 0)
          .toString();
        day.totals.refunded = new Decimal(day.totals.refunded || 0)
          .plus(record.refunded || 0)
          .toString();
        day.totals.rejected_stake_amount = new Decimal(day.totals.rejected_stake_amount || 0)
          .plus(record.rejected_stake_amount || 0)
          .toString();
        day.totals.lost_stake_amount = new Decimal(day.totals.lost_stake_amount || 0)
          .plus(record.lost_stake_amount || 0)
          .toString();
        day.totals.vendor_lost_stake_amount = new Decimal(day.totals.vendor_lost_stake_amount || 0)
          .plus(record.vendor_lost_stake_amount || 0)
          .toString();

        day.totals.vendor_stake_amount = new Decimal(day.totals.vendor_stake_amount || 0)
          .plus(record.vendor_stake_amount || 0)
          .toString();
        day.totals.vendor_max_payout = new Decimal(day.totals.vendor_max_payout || 0)
          .plus(record.vendor_max_payout || 0)
          .toString();
        day.totals.rejected_vendor_stake_amount = new Decimal(
          day.totals.rejected_vendor_stake_amount || 0
        )
          .plus(record.rejected_vendor_stake_amount || 0)
          .toString();
        day.totals.vendor_paid_out = new Decimal(day.totals.vendor_paid_out || 0)
          .plus(record.vendor_paid_out || 0)
          .toString();
        day.totals.vendor_refunded = new Decimal(day.totals.vendor_refunded || 0)
          .plus(record.vendor_refunded || 0)
          .toString();

        day.totals.rejected_count += record.rejected_count || 0;
        day.totals.accepted_count += record.accepted_count || 0;
        day.totals.stakes_won += record.stakes_won || 0;
        day.totals.stakes_lost += record.stakes_lost || 0;
        day.totals.stakes_voided += record.stakes_voided || 0;
        day.totals.stakes_waiting += record.stakes_waiting || 0;
      }

      // TODO Разобраться что это за дичь
      for (const key of Object.keys(days)) {
        // @ts-ignore
        daysUsers[key].delete(null);
        days[key].totals.users = daysUsers[key].size;
      }

      return days;
    },
    toggleSkipEmpty: {
      get() {
        return this.skipEmpty === 'true';
      },
      set(v: boolean) {
        this.skipEmpty = v ? 'true' : 'false';
      },
    },
    toggleSkipBanned: {
      get() {
        return this.skipBanned === 'true';
      },
      set(v: boolean) {
        this.skipBanned = v ? 'true' : 'false';
      },
    },
    toggleSkipNonCurrency: {
      get() {
        return this.skipNonCurrency === 'true';
      },
      set(v: boolean) {
        this.skipNonCurrency = v ? 'true' : 'false';
      },
    },
    toggleDetailSports: {
      get() {
        return this.detailSports === 'true';
      },
      set(v: boolean) {
        this.detailSports = v ? 'true' : 'false';
      },
    },
    toggleDetailCurrencies: {
      get() {
        return this.detailCurrencies === 'true';
      },
      set(v: boolean) {
        this.detailCurrencies = v ? 'true' : 'false';
      },
    },
    toggleDetailUsers: {
      get() {
        return this.detailUsers === 'true';
      },
      set(v: boolean) {
        this.detailUsers = v ? 'true' : 'false';
      },
    },
    selectPeriod: {
      get() {
        const periods = { day: 'По дням', month: 'По месяцам' };
        return periods[this.period] ?? null;
      },
      set(v: string) {
        switch (v) {
          case 'По дням':
            this.period = 'day';
            break;
          case 'По месяцам':
            this.period = 'month';
            break;
          default:
            console.error(`Period of "${v}" is not supported`);
        }
      },
    },
    inputUsers: {
      get() {
        return this.userIds ? this.userIds.join(',') : null;
      },
      set(v: string | null) {
        if (v) {
          this.userIds = (v as string)
            .replace(/[^\d,]+/, '')
            .split(',')
            .filter((c) => c)
            .map((c) => parseInt(c, 10));
        } else {
          this.userIds = null;
        }
      },
    },
  },
  data() {
    return {
      showStakesCount: true,
      showResults: false,
      showCalcCurrency: false,
      showCalcUSD: true,
      expansionFlags: new Array(100).fill(false),
      periodType: ['По дням', 'По месяцам'],
      periodForTable: 'month',
    };
  },
  methods: {
    formatCurrency,
    formatDate,
    formatMoney,
    ...mapActions(useStakesStore, ['fetchRecords', 'fetchRecordsCount']),
    ...mapActions(useCurrencyStore, ['fetchCurrencies']),
    flipPage(newOffset: number) {
      this.offset = newOffset;
      this.expansionFlags.fill(false);
      this.fetchRecords();
    },
    apply() {
      this.offset = 0;
      this.fetchRecords().then(() => (this.periodForTable = this.period));
      this.fetchRecordsCount();
    },
    reset() {
      this.offset = 0;
      this.fromDate = null;
      this.toDate = null;
      this.fetchRecords();
      this.fetchRecordsCount();
    },
    formatBalance(totals: any) {
      const amount =
        totals.vendor_stake_amount -
        totals.rejected_vendor_stake_amount -
        totals.vendor_paid_out -
        totals.vendor_refunded;
      const formatted = formatMoney(Math.abs(amount), 4, false);

      if (!formatted) return '';

      return `${amount >= 0 ? '+' : '-'}${formatted}`;
    },
  },
  mounted() {
    this.periodForTable = this.period;
    this.fetchCurrencies();

    if (!this.records) {
      this.fetchRecords();
      this.fetchRecordsCount();
    }
  },
});
</script>

<template>
  <template v-if="!records && !count && !offset">
    <div class="custom-background" style="height: 90vh">Нет записей или доступ ограничен</div>
  </template>
  <template v-else>
    <teleport to="#title">
      <headline class="mx-4 my-2">Статистика</headline>
    </teleport>
    <div
      class="inline-grid filters text-xs mb-4 mx-2"
      style="grid-template-columns: repeat(5, max-content)"
    >
      <Fieldset legend="Фильтры (UTC+0)" class="border-slate-600">
        <DatePicker
          v-model="fromDate"
          showIcon
          fluid
          iconDisplay="input"
          placeholder="Начало"
          class="my-2"
          dateFormat="dd.mm.yy"
        />
        <DatePicker
          v-model="toDate"
          showIcon
          fluid
          iconDisplay="input"
          placeholder="Конец"
          class="my-2"
          dateFormat="dd.mm.yy"
        />
        <MultiSelect
          v-model="currencies"
          display="chip"
          :options="getCurrencies"
          optionLabel="name"
          optionValue="name"
          filter
          placeholder="Валюты"
          class="w-full my-1"
        />
        <InputText
          type="text"
          v-model="inputUsers"
          fluid
          placeholder="ID пользователей через запятую"
          class="my-1 mb-3"
        />
      </Fieldset>
      <Fieldset legend="Группировки" class="wide">
        <Select
          name="period"
          v-model="selectPeriod"
          :options="periodType"
          placeholder="Периодичность"
          fluid
          class="my-2"
        />
        <!-- not effective since we have every day bets
        <div class="flex my-2 ml-1">
          <ToggleSwitch v-model="toggleSkipEmpty" size="sm" />
          <label class="ml-2 text-sm mt-0.5">Пропустить период без ставок</label>
        </div>
        -->
        <div class="flex my-2 ml-1">
          <ToggleSwitch v-model="toggleDetailCurrencies" size="sm" />
          <label class="ml-2 text-sm mt-0.5">Детализировать валюты</label>
        </div>
        <div class="flex my-2 ml-1">
          <ToggleSwitch v-model="toggleDetailUsers" size="sm" />
          <label class="ml-2 text-sm mt-0.5">Детализировать пользователей</label>
        </div>
      </Fieldset>
      <Fieldset legend="Фильтры">
        <div class="flex my-2 ml-1">
          <ToggleSwitch v-model="toggleSkipBanned" size="sm" />
          <label class="ml-2 text-sm mt-0.5">Исключить заблокированных</label>
        </div>
        <div class="flex my-2 ml-1">
          <ToggleSwitch v-model="toggleSkipNonCurrency" size="sm" />
          <label class="ml-2 text-sm mt-0.5">Исключить ставки в JET</label>
        </div>
      </Fieldset>
      <Fieldset legend="Колонки" class="wide">
        <div class="flex pl-2 pt-2">
          <ToggleSwitch v-model="showStakesCount" />
          <label class="ml-2 text-sm mt-0.5">Ставки и купоны, шт.</label>
        </div>
        <div class="flex pl-2 pt-2">
          <ToggleSwitch v-model="showResults" />
          <label class="ml-2 text-sm mt-0.5">Результаты ставок, шт</label>
        </div>
        <div class="flex pl-2 pt-2">
          <ToggleSwitch v-model="showCalcCurrency" />
          <label class="ml-2 text-sm mt-0.5">Расчет в валюте игрока</label>
        </div>
        <div class="flex pl-2 py-2">
          <ToggleSwitch v-model="showCalcUSD" />
          <label class="ml-2 text-sm mt-0.5">Расчет в USD</label>
        </div>
      </Fieldset>
      <div class="h-100 flex-col flex justify-end mx-1">
        <Button fluid class="btn btn-trans-dark mt-1" @click="reset">Сбросить</Button>
        <Button fluid class="btn btn-primary mt-1" @click="apply">Применить и обновить</Button>
      </div>
    </div>
    <section class="relative mx-3">
      <div v-if="loading" class="table-loader">
        <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
      </div>
      <table class="basic-table large">
        <thead>
          <tr>
            <th rowspan="2" style="width: 100px">Дата</th>
            <th rowspan="2" style="width: 110px">Игроков</th>

            <th colspan="4" v-if="showStakesCount">Ставки и купоны, шт</th>

            <th colspan="3" v-if="showResults">Результаты</th>

            <th colspan="7" v-if="showCalcCurrency">Расчет в валюте (для 1 валюты)</th>

            <th colspan="7" v-if="showCalcUSD">Расчет в USD</th>
          </tr>
          <tr>
            <template v-if="showStakesCount">
              <th>Купонов</th>
              <th>Ставок</th>
              <th>Принято</th>
              <th>Отказано</th>
            </template>

            <template v-if="showResults">
              <th>Win</th>
              <th>Lose</th>
              <th>Refund</th>
            </template>

            <template v-if="showCalcCurrency">
              <th>%</th>
              <th>Принято</th>
              <th>Макс.выигрыш</th>
              <th>Отказано</th>
              <th>Проиграно</th>
              <th>Выплаты</th>
              <th>Возврат</th>
            </template>

            <template v-if="showCalcUSD">
              <th>Принято</th>
              <th>Макс.выигрыш</th>
              <th>Отказано</th>
              <th>Проиграно</th>
              <th>Выплаты</th>
              <th>Возврат</th>
              <th>CoinsGame</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="({ totals, rows }, i) in Object.values(getRecordsGrouped)">
            <tr @click="expansionFlags[i] = !expansionFlags[i]">
              <td>{{ formatDate(totals.date, periodForTable) }}</td>
              <td>{{ totals.users }}</td>

              <template v-if="showStakesCount">
                <td>{{ totals.coupons }}</td>
                <td>{{ totals.stakes }}</td>
                <td>{{ totals.accepted_count }}</td>
                <td>{{ totals.rejected_count }}</td>
              </template>

              <template v-if="showResults">
                <td>{{ totals.stakes_won || '' }}</td>
                <td>{{ totals.stakes_lost || '' }}</td>
                <td>{{ totals.stakes_voided || '' }}</td>
              </template>

              <template v-if="showCalcCurrency">
                <td>{{ formatCurrency(totals.stakes_amount || null, '') }}</td>
                <td>{{ formatMoney(totals.stakes_amount || null, 4, false) }}</td>
                <td>{{ formatMoney(totals.max_payout, 4, false) }}</td>
                <td>{{ formatMoney(totals.rejected_stake_amount, 4, false) }}</td>
                <td>{{ formatMoney(totals.lost_stake_amount, 4, false) }}</td>
                <td>{{ formatMoney(totals.paid_out, 4, false) }}</td>
                <td>{{ formatMoney(totals.refunded, 4, false) }}</td>
              </template>

              <template v-if="showCalcUSD">
                <td class="r">
                  <span class="text-yellow-700 dark:text-yellow-500">
                    {{
                      formatMoney(
                        +totals.vendor_stake_amount - +totals.rejected_vendor_stake_amount,
                        4,
                        false
                      )
                    }}
                  </span>
                </td>
                <td class="r">{{ formatMoney(totals.vendor_max_payout, 4, false) }}</td>
                <td class="r">{{ formatMoney(totals.rejected_vendor_stake_amount, 4, false) }}</td>
                <td class="r">{{ formatMoney(totals.vendor_lost_stake_amount, 4, false) }}</td>
                <td class="r">
                  <span class="text-yellow-700 dark:text-yellow-500">
                    {{ formatMoney(totals.vendor_paid_out, 4, false) }}
                  </span>
                </td>
                <td class="r">
                  <span class="text-yellow-700 dark:text-yellow-500">
                    {{ formatMoney(totals.vendor_refunded, 4, false) }}
                  </span>
                </td>
                <td class="r">
                  <span
                    :class="
                      parseFloat(formatBalance(totals)) > 0 ? 'text-green-500' : 'text-red-500'
                    "
                  >
                    {{ formatBalance(totals) }}
                  </span>
                </td>
              </template>
            </tr>

            <template v-for="row in rows">
              <tr class="sub-table" v-show="expansionFlags[i]">
                <td>{{ row.currency }}</td>
                <td class="r">
                  <span v-if="!Array.isArray(row.user_id)" class="text-nowrap">
                    <router-link :to="{ name: 'detailed-bets', query: { userId: row.user_id } }" class="text-blue-500 underline">
                      {{ row.user_id }}
                    </router-link>
                    <clipboard-icon :text="row.user_id" class="ml-1" iconClass="text-[12px]"/>
                    <i
                      class="pi pi-external-link ml-1 text-[12px] opacity-40 hover:opacity-100 cursor-pointer"
                      @click="$router.push({ name: 'profile', params: { id: row.user_id }})"
                    />
                  </span>
                </td>

                <template v-if="showStakesCount">
                  <td>{{ row.coupons }}</td>
                  <td>{{ row.stakes }}</td>
                  <td>{{ row.accepted_count }}</td>
                  <td>{{ row.rejected_count }}</td>
                </template>

                <template v-if="showResults">
                  <td>{{ row.stakes_won || '' }}</td>
                  <td>{{ row.stakes_lost || '' }}</td>
                  <td>{{ row.stakes_voided || '' }}</td>
                </template>

                <template v-if="showCalcCurrency">
                  <td>{{ formatCurrency(row.stakes_amount || null, '') }}</td>
                  <td>{{ formatMoney(row.stakes_amount || null, 4, false) }}</td>
                  <td>{{ formatMoney(row.max_payout, 4, false) }}</td>
                  <td>{{ formatMoney(row.rejected_stake_amount, 4, false) }}</td>
                  <td>{{ formatMoney(row.lost_stake_amount, 4, false) }}</td>
                  <td>{{ formatMoney(row.paid_out, 4, false) }}</td>
                  <td>{{ formatMoney(row.refunded, 4, false) }}</td>
                </template>

                <template v-if="showCalcUSD">
                  <td class="r">
                    <span class="text-yellow-700 dark:text-yellow-500">
                      {{
                        formatMoney(
                          +row.vendor_stake_amount - +row.rejected_vendor_stake_amount,
                          4,
                          false
                        )
                      }}
                    </span>
                  </td>
                  <td class="r">{{ formatMoney(row.vendor_max_payout, 4, false) }}</td>
                  <td class="r">{{ formatMoney(row.rejected_vendor_stake_amount, 4, false) }}</td>
                  <td class="r">{{ formatMoney(row.vendor_lost_stake_amount, 4, false) }}</td>
                  <td class="r">
                    <span class="text-yellow-700 dark:text-yellow-500">
                      {{ formatMoney(row.vendor_paid_out, 4, false) }}
                    </span>
                  </td>
                  <td class="r">
                    <span class="text-yellow-700 dark:text-yellow-500">
                      {{ formatMoney(row.vendor_refunded, 4, false) }}
                    </span>
                  </td>
                  <td class="r">
                    <span
                      :class="
                        parseFloat(formatBalance(row)) > 0 ? 'text-green-500' : 'text-red-500'
                      "
                    >
                      {{ formatBalance(row) }}
                    </span>
                  </td>
                </template>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
      <div class="m-4 mt-6 w-100 text-center justify-center">
        <pagination :offset="offset" :count="count" :size="size" @flip="flipPage" />
      </div>
    </section>
  </template>
  <!-- TODO remove debug output -->
  <!--  <div v-for="group in getRecordsGrouped">-->
  <!--    <div><h3>{{ group.totals.date }}</h3></div>-->
  <!--    <div><h4>{{ group.totals }}</h4></div>-->
  <!--    <div v-for="r in group.rows">-->
  <!--      {{ r }}-->
  <!--    </div>-->
  <!--  </div>-->
</template>

<style lang="scss">
.large {
  td, th {
    &.r {
      text-align: right;
      font-family: monospace;
    }
    @apply text-sm p-3 text-gray-600 dark:text-gray-400;
  }
  tr.sub-table {
    td {
      @apply px-3 py-1;
    }
    @apply bg-gray-200 dark:bg-gray-900;
    @apply hover:bg-gray-300 dark:hover:bg-gray-800;
    opacity: 80%;
  }
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 1s ease;
}
.slide-enter-to,
.slide-leave-from {
  max-height: 500px; /* максимальная высота блока, подбери по контенту */
  opacity: 1;
}

th {
  @apply bg-gray-300 dark:bg-gray-800 border border-gray-400 dark:border-gray-600;
}

td {
  @apply border border-dotted border-gray-700;

  &[data-pc-section='rowexpansioncell'] {
    @apply p-0;
    & tr:first-child > th {
      border-top: none;
    }

    & tr:last-child > td {
      border-bottom: none;
    }

    & tr :is(th, td):first-child {
      border-left: none;
    }

    & tr :is(th, td):last-child {
      border-right: none;
    }

    & td {
      @apply bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 py-1;
    }

    & th {
      @apply bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-0;
    }
  }
}
</style>
